// Fraction class that represents a rational number
// @author zz85
// @author incompleteopus (modifications)
import Rhythm from './rhythm';

class Fraction {
  numerator!: number;

  denominator!: number;

  /**
   * Create a new fraction, default to 1/1 if nothing provided
   */
  constructor(numerator?: number, denominator?: number) {
    this.set(numerator, denominator);
  }

  /**
   * Sets the fraction's value, defaults to 1/1
   */
  set(numerator: number = 1, denominator: number = 1) {
    this.numerator = numerator;
    this.denominator = denominator;
    return this;
  }

  /**
   * @returns fraction expressed as number type
   */
  value() {
    return this.numerator / this.denominator;
  }

  simplify() {
    let u = this.numerator;
    let d = this.denominator;

    const gcd = Fraction.gcd(u, d);
    u /= gcd;
    d /= gcd;

    if (d < 0) {
      d = -d;
      u = -u;
    }
    return this.set(u, d);
  }

  add(numerator: number | undefined, denominator: number | undefined): this;
  add(numberOrFraction: number | Fraction): this;
  add(param1: number | undefined | Fraction, param2?: number) {
    let otherNumerator;
    let otherDenominator;

    if (param1 instanceof Fraction) {
      otherNumerator = param1.numerator;
      otherDenominator = param1.denominator;
    } else {
      if (param1 !== undefined) {
        otherNumerator = param1;
      } else {
        otherNumerator = 0;
      }

      if (param2 !== undefined) {
        otherDenominator = param2;
      } else {
        otherDenominator = 1;
      }
    }

    const lcm = Fraction.lcm(this.denominator, otherDenominator);
    const a = lcm / this.denominator;
    const b = lcm / otherDenominator;

    const u = this.numerator * a + otherNumerator * b;
    return this.set(u, lcm);
  }

  subtract(numerator: number | undefined, denominator: number | undefined): this;
  subtract(numberOrFraction: number | Fraction): this;
  subtract(param1?: number | Fraction, param2?: number) {
    let otherNumerator = 0;
    let otherDenominator = 1;

    if (param1 instanceof Fraction) {
      otherNumerator = param1.numerator;
      otherDenominator = param1.denominator;
    } else {
      if (param1 !== undefined) {
        otherNumerator = param1;
      } else {
        otherNumerator = 0;
      }

      if (param2 !== undefined) {
        otherDenominator = param2;
      } else {
        otherDenominator = 1;
      }
    }

    const lcm = Fraction.lcm(this.denominator, otherDenominator);
    const a = lcm / this.denominator;
    const b = lcm / otherDenominator;

    const u = this.numerator * a - otherNumerator * b;
    return this.set(u, lcm);
  }

  multiply(numerator: number | undefined, denominator: number | undefined): this;
  multiply(numberOrFraction: number | Fraction): this;
  multiply(param1?: number | Fraction, param2?: number) {
    let otherNumerator = 1;
    let otherDenominator = 1;

    if (param1 instanceof Fraction) {
      otherNumerator = param1.numerator;
      otherDenominator = param1.denominator;
    } else {
      if (param1 !== undefined) {
        otherNumerator = param1;
      } else {
        otherNumerator = 1;
      }

      if (param2 !== undefined) {
        otherDenominator = param2;
      } else {
        otherDenominator = 1;
      }
    }

    return this.set(this.numerator * otherNumerator, this.denominator * otherDenominator);
  }

  divide(numerator: number | undefined, denominator: number | undefined): this;
  divide(numberOrFraction: number | Fraction): this;
  divide(param1?: number | Fraction, param2?: number) {
    let otherNumerator = 1;
    let otherDenominator = 1;

    if (param1 instanceof Rhythm) {
      param1 = param1.getFraction();
    }
    if (param1 instanceof Fraction) {
      otherNumerator = param1.numerator;
      otherDenominator = param1.denominator;
    } else {
      if (param1 !== undefined) {
        otherNumerator = param1;
      } else {
        otherNumerator = 1;
      }

      if (param2 !== undefined) {
        otherDenominator = param2;
      } else {
        otherDenominator = 1;
      }
    }

    return this.set(this.numerator * otherDenominator, this.denominator * otherNumerator);
  }

  /**
   * Returns remainder following division by comparison fraction as a fraction
   * @param {Fraction} compare
   */
  modulo(compare: Fraction | number): Fraction {
    const divisor = Fraction._compareA.copy(compare).simplify();
    const original = Fraction._compareB.copy(this).simplify();

    const originalDenominator = original.denominator;

    // get to a common denominator:
    original.numerator *= divisor.denominator;
    original.denominator *= divisor.denominator;
    divisor.numerator *= originalDenominator;
    divisor.denominator *= originalDenominator;

    // Perform the modulus on the numerator:
    original.numerator %= divisor.numerator;

    // Simplify, if possible, and return:
    return original.simplify();
  }

  /**
   * Simplifies both sides and checks if they are equal.
   */
  equals(numberOrFraction: number | Fraction): boolean;
  equals(compare: Fraction | number) {
    if (!(compare instanceof Fraction)) {
      compare = new Fraction(compare);
    }

    const a = Fraction._compareA.copy(compare).simplify();
    const b = Fraction._compareB.copy(this).simplify();

    return a.numerator === b.numerator && a.denominator === b.denominator;
  }

  greaterThan(numberOrFraction: number | Fraction): boolean;
  greaterThan(compare: Fraction | number) {
    const a = Fraction._compareB.copy(this);
    a.subtract(compare);
    return a.numerator > 0;
  }

  greaterThanEquals(numberOrFraction: number | Fraction): boolean;
  greaterThanEquals(compare: Fraction | number) {
    const a = Fraction._compareB.copy(this);
    a.subtract(compare);
    return a.numerator >= 0;
  }

  lessThan(numberOrFraction: number | Fraction): boolean;
  lessThan(compare: Fraction | number) {
    return !this.greaterThanEquals(compare);
  }

  lessThanEquals(numberOrFraction: number | Fraction): boolean;
  lessThanEquals(compare: Fraction | number) {
    return !this.greaterThan(compare);
  }

  /**
   * Creates a new copy with this current values.
   */
  clone() {
    return new Fraction(this.numerator, this.denominator);
  }

  /**
   * Copies value of another Fraction into itself.
   */
  copy(copy: Fraction | number) {
    if (typeof copy === 'number') {
      this.set(copy, 1);
      return this;
    }
    return this.set(copy.numerator, copy.denominator);
  }

  /**
   * Returns the integer component eg. (4/2) == 2
   */
  quotient() {
    return Math.floor(this.numerator / this.denominator);
  }

  /**
   * Returns the fraction component when reduced to a mixed number
   */
  fraction() {
    return this.numerator % this.denominator;
  }

  /**
   * Sets the absolute value
   */
  abs() {
    this.denominator = Math.abs(this.denominator);
    this.numerator = Math.abs(this.numerator);
    return this;
  }

  /**
   * Returns a raw string representation numerator/denominator
   */
  toString() {
    return `${this.numerator}/${this.denominator}`;
  }

  /**
   * Returns number type representation
   */
  valueOf() {
    return this.value();
  }

  /**
   * Returns a simplified string respresentation (2/4 becomes '1/2')
   */
  toSimplifiedString() {
    return Fraction._tmp.copy(this).simplify().toString();
  }

  /**
   * Returns string representation in mixed form '3 2/3'
   */
  toMixedString() {
    let s = '';
    const q = this.quotient();
    const f = this.clone();

    f.abs().subtract(q);

    if (q !== 0) {
      s += q;

      if (f.numerator !== 0) {
        s += ` ${f.toSimplifiedString()}`;
      }
    } else if (f.numerator === 0) {
      s = '0';
    } else {
      s = f.toSimplifiedString();
    }

    return s;
  }

  /**
   * Parses a fraction string like '5/3'; does not accept a mixed string like '4 1/8'.
   * @param str
   */
  parse(str: string) {
    const i = str.split('/');
    const n = parseInt(i[0]!, 10);
    const d = i[1] ? parseInt(i[1], 10) : 1;

    return this.set(n, d);
  }

  /**
   * GCD: Find greatest common divisor using Euclidean algorithm
   */
  static gcd(a: number, b: number): number {
    if (typeof a !== 'number' || typeof b !== 'number') {
      throw new Error(`BadArgument: Invalid numbers: ${a}, ${b}`);
    }

    let t;

    while (b !== 0) {
      t = b;
      b = a % b;
      a = t;
    }

    return a;
  }

  /**
   * LCM: Lowest common multiple
   */
  static lcm(a: number, b: number) {
    return (a * b) / Fraction.gcd(a, b);
  }

  /**
   * LCMM: Lowest common multiple for more than two numbers
   */
  static lcmm(numbers: number[]): number {
    if (numbers.length === 0) {
      return 0;
    }
    if (numbers.length === 1) {
      return numbers[0]!;
    }
    if (numbers.length === 2) {
      return Fraction.lcm(numbers[0]!, numbers[1]!);
    }
    const arg0 = numbers[0]!;
    numbers.shift();
    return Fraction.lcm(arg0, Fraction.lcmm(numbers));
  }

  // Temporary cached objects
  private static _compareA = new Fraction();

  private static _compareB = new Fraction();

  private static _tmp = new Fraction(0);
}

export default Fraction;
